import React, { memo, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { useThemeColors, useThemeStore } from '../../shared/theme';
import { useRtl } from '../../shared/i18n/rtl';
import { resolveLoadMainPalette } from './resolveLoadMainPalette';
import type { LoadMainProps, LoadMainVariant, LoadMainVariantProps } from './types';
import { BarsLoader } from './variants/BarsLoader';
import { DotsLoader } from './variants/DotsLoader';
import { HourglassLoader } from './variants/HourglassLoader';
import { PulseLoader } from './variants/PulseLoader';
import { RingLoader } from './variants/RingLoader';
import { SphereLoader } from './variants/SphereLoader';
import { SpinnerLoader } from './variants/SpinnerLoader';

const VARIANT_DURATION_SCALE: Record<LoadMainVariant, number> = {
  spinner: 1,
  ring: 1.05,
  hourglass: 1.35,
  dots: 1.1,
  pulse: 1.2,
  bars: 1.05,
  sphere: 2.4,
};

const VARIANT_RENDERERS: Record<
  LoadMainVariant,
  React.ComponentType<LoadMainVariantProps>
> = {
  spinner: SpinnerLoader,
  ring: RingLoader,
  hourglass: HourglassLoader,
  dots: DotsLoader,
  pulse: PulseLoader,
  bars: BarsLoader,
  sphere: SphereLoader,
};

export const LoadMain = memo(function LoadMain({
  variant = 'pulse',
  size = 32,
  color,
  trackColor,
  loopDurationMs = 1100,
  accessibilityLabel = 'Loading',
  accessibilityRole = 'progressbar',
  style,
}: LoadMainProps) {
  const colors = useThemeColors();
  const appearance = useThemeStore(s => s.appearance);
  const palette = useMemo(
    () => resolveLoadMainPalette(colors, appearance, color, trackColor),
    [appearance, color, colors, trackColor],
  );
  const is_rtl = useRtl().isRTL;
  const duration_ms = Math.round(loopDurationMs * VARIANT_DURATION_SCALE[variant]);
  const VariantComponent = VARIANT_RENDERERS[variant];

  const container_style = useMemo(
    () => [
      styles.container,
      {
        width: size,
        height: size,
      },
      StyleSheet.flatten(style),
    ],
    [size, style],
  );

  const is_accessible =
    accessibilityLabel != null &&
    accessibilityLabel !== '' &&
    accessibilityRole != null;

  return (
    <View
      style={container_style}
      accessible={is_accessible}
      accessibilityRole={is_accessible ? accessibilityRole : undefined}
      accessibilityLabel={is_accessible ? accessibilityLabel : undefined}
      accessibilityState={is_accessible ? { busy: true } : undefined}
      importantForAccessibility={is_accessible ? 'yes' : 'no-hide-descendants'}>
      <VariantComponent
        size={size}
        colors={palette}
        duration_ms={duration_ms}
        is_rtl={is_rtl}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
